/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    ActionButton,
    Alert,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef, Row } from "@tanstack/react-table";
import {
    Link,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
    ClaimRelationshipToSubscriber,
    ClaimsServiceDetails,
    ClaimStatus,
    IGetClaimsAttachmentResponse,
    SingleClaim as ISingleClaim,
} from "../../types/claims.types";
import {
    addPracticeParamToUrl,
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    userInitials,
} from "../../../../utils/helpers";
// import Table from "../../../../components/Table/Table";
import styles from "./SingleClaim.module.scss";

import { useFetchDiagnosisCodes } from "../../../../hooks/queries";
import {
    useChangeClaimVoidStatus,
    useCreateDraftClaim,
    useDeleteClaimsAttachments,
    useUpdateClaimStatus,
} from "../../hooks/claims.mutations";
import useToast from "../../../../hooks/useToast";
import { getStateInShortForm } from "../../../../utils/helpers/us-states/us-states";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import PlusIcon from "../../../../components/Icons/Plus";
import FilePicker from "../../../../components/FilePicker/FilePicker";
import { SupportedUploadMethod } from "../../../../utils/types/filepicker";
import { ClientDocumentTypes } from "../../../clients/types/clients.types";
import {
    useFetchClaimsAttachments,
    useGetParentClaim,
} from "../../hooks/claims.queries";
import { auth } from "../../../../firebase";
import { useAppSelector } from "../../../../hooks/useRedux";
import DeleteIcon from "../../../../icons/Delete";
import { ModifierTypes } from "../../../notes/types/notes.types";

interface SingleClaimsProps {
    claim: ISingleClaim;
    isCurrent: boolean;
}
export default function SingleClaim({ claim, isCurrent }: SingleClaimsProps) {
    const baseURL = import.meta.env.VITE_API_BASE_URL;

    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId, noteId } = params;
    const currentClientId = clientId || claim.client_id;
    const currentNoteId = noteId || claim.note_id;

    const [uploadNewDoc, setUploadNewDoc] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = React.useState("");
    const [showPrompt, setShowPrompt] = React.useState({
        show: false,
        promptAction: "",
    });

    const { practice } = useAppSelector((state) => state.userPractice);

    const queryClient = useQueryClient();

    // Toast for success and error states
    const { toast } = useToast();

    const diagnosisCodes = useFetchDiagnosisCodes();
    const regenerateDraftClaim = useCreateDraftClaim();
    const deleteClaimsAttachment = useDeleteClaimsAttachments();

    const fetchClaimsAttachments = useFetchClaimsAttachments({
        claim_id: claim.claim_id,
    });

    const claimNoteDetails = useFetchSessionNote(
        currentClientId || "",
        currentNoteId || "",
        Boolean(currentClientId) && Boolean(currentNoteId)
    );

    // hook for voiding/unvoiding claim
    const changeVoidStatus = useChangeClaimVoidStatus();

    // function to download claims attachments
    const downloadFile = async (claim_url: string, file_name: string) => {
        const token = await auth.currentUser?.getIdToken();
        setIsLoading(true);

        try {
            const response = await fetch(
                `${baseURL}/file?file_url=${claim_url}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        practice_id: practice?.practice_id || "",
                    },
                }
            );

            if (response) {
                const data = await response.json();
                const base64Data = data.data;
                const contentType = data.content_type;

                // Determine file extension based on content type
                let fileExtension;
                if (contentType === "application/pdf") {
                    fileExtension = "pdf";
                } else {
                    fileExtension = "tiff";
                }

                // Convert the Base64 string to a binary Blob
                const binaryData = atob(base64Data);
                const byteArray = new Uint8Array(binaryData.length);
                // eslint-disable-next-line no-plusplus
                for (let i = 0; i < binaryData.length; i++) {
                    byteArray[i] = binaryData.charCodeAt(i);
                }
                const blob = new Blob([byteArray], { type: contentType });

                // Create a download link
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `${
                    file_name || "Downloaded file"
                }.${fileExtension}`;
                link.style.display = "none";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            toast({
                mode: "error",
                message: "Cannot download attachment at this time",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // function to delete claims attachments
    const handleDeleteClaimsAttachments = (claimId: string) => {
        const deleteAttachmentPayload = {
            claim_attachment_ids: [claimId],
        };
        deleteClaimsAttachment.mutate(deleteAttachmentPayload, {
            onSuccess: async (response) => {
                toast({
                    mode: "success",
                    message:
                        response?.message ||
                        "Claim attachment deleted successfully",
                });

                queryClient.invalidateQueries({
                    queryKey: ["get-claims-attachment"],
                });
                setShowPrompt({
                    show: false,
                    promptAction: "",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Cannot delete attachment at this time",
                });
                setShowPrompt({
                    show: false,
                    promptAction: "",
                });
            },
        });
    };

    const columns: ColumnDef<ClaimsServiceDetails>[] = React.useMemo(() => {
        // columns items for Procedures, Services or Supplies

        const procedureColumns = [
            {
                accessorKey: "cpt",
                header: "CPT/HCPCS",
                // eslint-disable-next-line react/no-unused-prop-types
                cell: ({ row }: { row: Row<ClaimsServiceDetails> }) => (
                    <div>{row.original.procedureCode || "--"}</div>
                ),
            },
        ];

        const serviceDetails = claim?.claim_content?.serviceDetails || [];
        const modifiers = serviceDetails.map(
            (item) => item.modifiers || ["", "", "", ""]
        );

        if (modifiers.length > 0) {
            const modifierHeaders = [
                "Modifier A",
                "Modifier B",
                "Modifier C",
                "Modifier D",
            ];

            for (
                let modIndex = 0;
                modIndex < modifierHeaders.length;
                modIndex += 1
            ) {
                procedureColumns.push({
                    accessorKey: `modifier_${modIndex}`,
                    header: modifierHeaders[modIndex],
                    cell: ({ row }) => {
                        const serviceIndex = serviceDetails.findIndex(
                            (s) =>
                                s.procedureCode === row.original.procedureCode
                        );
                        const modifierCode =
                            modifiers[serviceIndex]?.[modIndex] || "--";

                        const modifierEntry = Object.values(ModifierTypes).find(
                            (mod) => mod.startsWith(`${modifierCode} `)
                        );

                        const modifierDescription = modifierEntry
                            ? modifierEntry.replace(modifierCode, "").trim()
                            : "";

                        return (
                            <div className="w-[200px] text-wrap">
                                {modifierCode}{" "}
                                {modifierDescription
                                    ? `- ${modifierDescription}`
                                    : ""}
                            </div>
                        );
                    },
                });
            }
        }

        return [
            {
                header: "Date(s) of service:",
                columns: [
                    {
                        accessorKey: "from",
                        header: "FROM",
                        cell: () => (
                            <div className="w-[100px]">
                                {claimNoteDetails.data?.data
                                    ? formatDate(
                                          claimNoteDetails.data?.data
                                              .date_of_service
                                      )
                                    : "--"}
                            </div>
                        ),
                    },
                    {
                        accessorKey: "collected",
                        header: "TO",
                        cell: () => (
                            <div className="w-[100px]">
                                {claimNoteDetails.data?.data
                                    ? formatDate(
                                          claimNoteDetails.data?.data
                                              .date_of_service
                                      )
                                    : "--"}
                            </div>
                        ),
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "pos",
                        header: "Place of service code",
                        cell: ({ row }) => (
                            <div className="w-[200px] text-wrap">
                                {row.original.placeOfServiceCode || "--"}
                            </div>
                        ),
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "emg",
                        header: "EMG",
                        cell: ({ row }) =>
                            row.original.isEmergency === "Y" ? "Yes" : "No",
                    },
                ],
            },
            {
                header: "Procedures, Services or Supplies",
                columns: procedureColumns,
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "diagnosis",
                        header: "Diagnosis Pointer",
                        cell: ({ row }) =>
                            row.original.diagnosisPointers
                                .map((e: string) => e)
                                .join(", ") || "--",
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "charges",
                        header: "Charges",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(row.original.charge) || "--",
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "units",
                        header: "Days or Units",
                        cell: ({ row }) => row.original.units || "--",
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "family_plan",
                        header: "EPSDT Family Plan",
                        cell: ({ row }) => row.original.isFamilyPlan || "--",
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "id",
                        header: "ID Qual.",
                        cell: ({ row }) =>
                            row.original.renderingProviderNumQualifier || "--",
                    },
                ],
            },
            {
                header: " ",
                columns: [
                    {
                        accessorKey: "provider_id",
                        header: "Rendering Provider ID #",
                        cell: ({ row }) =>
                            row.original.renderingProviderNPI || "--",
                    },
                ],
            },
        ];
    }, [claimNoteDetails.data?.data, claim?.claim_content?.serviceDetails]);

    const attachedDocumentsColumns: ColumnDef<IGetClaimsAttachmentResponse>[] =
        [
            {
                accessorKey: "date_uploaded",
                header: "DATE UPLOADED",
                cell: ({ row }) => (
                    <span>
                        {`${formatDate(
                            row.original.created_at as string,
                            true,
                            "MMM d, yyyy"
                        )}` || "--"}
                    </span>
                ),
            },
            {
                accessorKey: "document_name",
                header: "DOCUMENT NAME",
                cell: ({ row }) => <span>{row.original.name || "--"}</span>,
            },
            {
                accessorKey: "icons",
                header: "",
                cell: ({ row }) => (
                    <div className="flex w-full justify-end gap-2">
                        <ActionButton
                            variant="can-hover"
                            size="auto"
                            className="h-7"
                            onClick={() => {
                                downloadFile(
                                    row.original.claim_attachment_url,
                                    row.original.name
                                );
                            }}
                            disabled={isLoading}
                            actionTitle="Download"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="size-3.5 shrink-0"
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        </ActionButton>

                        {claim?.claim_status ===
                        ClaimStatus.READY_FOR_SUBMISSION ? (
                            <ActionButton
                                variant="can-hover"
                                size="auto"
                                className="h-7"
                                onClick={() => {
                                    setShowPrompt({
                                        ...showPrompt,
                                        show: true,
                                        promptAction: "delete",
                                    });
                                    setAttachmentToDelete(
                                        row.original.claim_attachment_id
                                    );
                                }}
                                actionTitle="Delete"
                            >
                                <DeleteIcon />
                            </ActionButton>
                        ) : null}
                    </div>
                ),
            },
        ];

    const reformatSubscriberBirthdate = () => {
        if (!claim.claim_content.subscriberBirthdate) return null;

        const [month, day, year] =
            claim.claim_content.subscriberBirthdate.split("/");

        if (!month || !day || !year) return null;

        return `${year}-${month}-${day}`;
    };

    const finalSubscriberDate = convertDateFilterStringToDate(
        reformatSubscriberBirthdate()
    );

    const handleEditClick = () => {
        searchParams.set("claims_view", "edit");
        searchParams.set("claim_id", claim.claim_id);
        setSearchParams(searchParams);
    };

    const handleViewClaim = () => {
        navigate(`/claim/${claim.claim_id}/print`);
    };

    // Mapping object for relationship display
    const relationshipMapping: { [key: string]: string } = {
        [ClaimRelationshipToSubscriber.Self]: "Self",
        [ClaimRelationshipToSubscriber.Spouse]: "Spouse",
        [ClaimRelationshipToSubscriber.Child]: "Child",
        [ClaimRelationshipToSubscriber.Other]: "Other",
    };

    // get all the claims diagnosis and add them in an array return only the ones that are not empty, null or undefined
    const claimsDiagnosis = [
        { code: claim?.claim_content?.diagnosisCode1, pointer: "A" },
        { code: claim?.claim_content?.diagnosisCode2, pointer: "B" },
        { code: claim?.claim_content?.diagnosisCode3, pointer: "C" },
        { code: claim?.claim_content?.diagnosisCode4, pointer: "D" },
        { code: claim?.claim_content?.diagnosisCode5, pointer: "E" },
        { code: claim?.claim_content?.diagnosisCode6, pointer: "F" },
        { code: claim?.claim_content?.diagnosisCode7, pointer: "G" },
        { code: claim?.claim_content?.diagnosisCode8, pointer: "H" },
        { code: claim?.claim_content?.diagnosisCode9, pointer: "I" },
        {
            code: claim?.claim_content?.diagnosisCode10,
            pointer: "J",
        },
        {
            code: claim?.claim_content?.diagnosisCode11,
            pointer: "K",
        },
        {
            code: claim?.claim_content?.diagnosisCode12,
            pointer: "L",
        },
    ].filter(
        (diagnosis) =>
            diagnosis.code !== undefined &&
            diagnosis.code !== null &&
            diagnosis.code !== ""
    );

    // get diagnosis and its decription
    const getDiagnosisCodes = () => {
        if (
            claimsDiagnosis &&
            claimsDiagnosis.length > 0 &&
            diagnosisCodes &&
            diagnosisCodes.data
        ) {
            return claimsDiagnosis
                .map((diagnosis) => {
                    const matchingCode = diagnosisCodes?.data?.data?.find(
                        (code) => code.code === diagnosis.code
                    );
                    if (matchingCode) {
                        return {
                            code: matchingCode.code,
                            description: matchingCode.description,
                            pointer: diagnosis.pointer,
                        };
                    }
                    return null;
                })
                .filter(Boolean); // Remove any null values from the array
        }
        return [];
    };

    // hook for updating claim status
    const patchClaimStatus = useUpdateClaimStatus();

    const claimParent = useGetParentClaim(
        claim.parent_claim_id ?? "",
        Boolean(claim.parent_claim_id)
    );
    const isInMultiClaimGroup =
        (claimParent.data?.data?.child_claims?.length ?? 0) > 1;

    // function to change claim status
    const handleChangeStatus = (claimStatus: ClaimStatus) => {
        if (claimStatus === ClaimStatus.SUBMITTED) {
            let errorMessage = "";

            if (claim.claim_content?.patientState.length !== 2) {
                errorMessage = `Invalid patient state`;
            }

            if (!claim.claim_content.subscriberCity) {
                errorMessage = errorMessage
                    ? `${errorMessage}, Subscriber city is missing`
                    : `Subscriber city is missing`;
            }

            if (!claim.claim_content.patientCity) {
                errorMessage = errorMessage
                    ? `${errorMessage}, Patient city is missing`
                    : `Patient city is missing`;
            }

            if (
                !claim.claim_content.referringProviderNPI ||
                claim.claim_content.referringProviderNPI?.length !== 10
            ) {
                errorMessage = errorMessage
                    ? `${errorMessage}, Provider NPI should be 10 digits`
                    : `Provider NPI should be 10 digits`;
            }

            if (
                claim?.claim_content?.patientRelationshipToSubscriber === "18"
            ) {
                if (
                    `${claim.claim_content.subscriberFirstName} ${claim.claim_content.subscriberLastName}`.toLowerCase() !==
                    `${claim.claim_content?.patientFirstName} ${claim.claim_content?.patientLastName}`.toLowerCase()
                ) {
                    errorMessage = errorMessage
                        ? `${errorMessage}, Patient name must be the same as subscriber name when patient relationship to insured is "SELF."`
                        : `Patient name must be the same as subscriber name when patient relationship to insured is "SELF.`;
                }
            }

            if (errorMessage) {
                toast({
                    mode: "error",
                    message: (
                        <>
                            {errorMessage.split(",").map((msg) => (
                                <span
                                    key={msg}
                                    className="block leading-loose font-medium"
                                >
                                    {msg}
                                </span>
                            ))}
                        </>
                    ),
                    duration: 10000,
                });
                return;
            }
        }

        const changeStatusPayload = {
            claim_id: claim.claim_id,
            claim_status: claimStatus,
        };
        patchClaimStatus.mutate(changeStatusPayload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries([
                    "get-claim",
                    { claim_id: claim.claim_id },
                ]);

                queryClient.invalidateQueries([
                    "get-claim",
                    { note_id: noteId },
                ]);
                toast({
                    mode: "success",
                    message:
                        response?.message ||
                        "Claim status updated successfully",
                });
                searchParams.set("status", claimStatus);
                setSearchParams(searchParams);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update claim status at this time",
                });
            },
        });
    };

    // function to change claim status
    const handleRegenerateClaims = (overwrite: boolean) => {
        const changeStatusPayload = {
            note_id: noteId || "",
            overwrite_existing: overwrite,
        };
        regenerateDraftClaim.mutate(changeStatusPayload, {
            onSuccess: async (response) => {
                const newClaimId = response.data.claim.claim_id;

                // Get the current URL
                let currentUrl = location.pathname;

                searchParams.set("status", response.data.claim.claim_status);

                searchParams.set(
                    "active_claim_id",
                    response.data.claim.claim_id
                );

                // Update the claim ID in the URL
                currentUrl = currentUrl.replace(
                    /\/claims\/([^/]+)/,
                    `/claims/${newClaimId}`
                );

                // Navigate to the updated URL
                toast({
                    mode: "success",
                    message: `Claim updated successfully`,
                });

                queryClient.removeQueries({ queryKey: ["claims"] });
                queryClient.removeQueries({ queryKey: ["get-claim"] });
                setShowPrompt({ ...showPrompt, show: false });
                navigate(`${currentUrl}?${searchParams.toString()}`, {
                    replace: true,
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Cannot update claim at this time",
                });
            },
        });
    };

    // Function to change claim void status
    const handleChangeClaimVoidStatus = (status: boolean) => {
        const payload = {
            claim_ids: [claim.claim_id],
            void_status: status,
        };

        changeVoidStatus.mutate(payload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries([
                    "get-claim",
                    { claim_id: claim.claim_id },
                ]);

                queryClient.invalidateQueries([
                    "get-claim",
                    { note_id: noteId },
                ]);
                queryClient.refetchQueries([
                    "claim-submission-history",
                    claim.claim_id,
                ]);

                toast({
                    mode: "success",
                    message: response?.data.payer_voided_claims?.length
                        ? "Payer void claim created and ready for submission"
                        : response?.message || "Claim unvoided successfully",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.data?.unvoided_claims?.[0]?.reason ||
                        err.response?.data.message ||
                        "Could not unvoid claim at this time",
                });
            },
        });
    };

    return (
        <div>
            <Dialog open={showPrompt.show}>
                <DialogContent
                    variant="center"
                    title={
                        showPrompt.promptAction === "delete"
                            ? "Delete"
                            : "Reset claim"
                    }
                    showFooter
                    cancelText={
                        showPrompt.promptAction === "delete"
                            ? "Cancel"
                            : "Update Current Claim"
                    }
                    onCancelClick={
                        showPrompt.promptAction === "delete"
                            ? undefined
                            : () => handleRegenerateClaims(false)
                    }
                    saveText={
                        showPrompt.promptAction === "delete"
                            ? "Delete File"
                            : "Create New Claim"
                    }
                    onSaveClick={() => {
                        if (showPrompt.promptAction === "delete") {
                            handleDeleteClaimsAttachments(attachmentToDelete);
                        } else {
                            handleRegenerateClaims(true);
                        }
                    }}
                    handleCloseDialog={() =>
                        setShowPrompt({
                            ...showPrompt,
                            show: false,
                        })
                    }
                    isSubmitBtnDisabled={
                        regenerateDraftClaim.isLoading ||
                        deleteClaimsAttachment.isLoading
                    }
                    isCancelBtnDisabled={
                        regenerateDraftClaim.isLoading ||
                        deleteClaimsAttachment.isLoading
                    }
                >
                    <div className="flex flex-col gap-y-3 mb-8">
                        <h3 className="text-title font-semibold text-text">
                            {showPrompt.promptAction === "delete"
                                ? " Are you sure you want to delete document?"
                                : "Are you sure you want to reset this claim?"}
                        </h3>
                        <p className="text-base font-normal text-textSec">
                            {showPrompt.promptAction === "delete"
                                ? "By confirming, please note that this action is irreversible"
                                : "This will replace all claim information with the latest details from the client, provider, and practice profiles. Any changes you made to this claim manually will be lost."}
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={uploadNewDoc}>
                <DialogContent
                    title="Attach a new document"
                    variant="center"
                    handleCloseDialog={() => setUploadNewDoc(false)}
                    showFooter={false}
                >
                    <FilePicker
                        name="filepicker"
                        uploadMethods={[
                            {
                                method: SupportedUploadMethod.manualupload,
                            },
                            {
                                method: SupportedUploadMethod.googledrive,
                            },
                            {
                                method: SupportedUploadMethod.dropbox,
                            },
                        ]}
                        documentToUploadType={ClientDocumentTypes.OTHER}
                        uploadType="claims-file"
                        actionText="Attach document"
                        showCancelButton
                        claimId={claim.claim_id}
                        cancelBtnClick={() => setUploadNewDoc(false)}
                        successCallback={() => {
                            setUploadNewDoc(false);
                        }}
                        accept=".pdf, .tiff"
                        acceptedTypes=".pdf, .tiff"
                        dropzoneAcceptedTypes={{
                            "application/*": [".pdf", ".tiff"],
                        }}
                        googleDriveViewIdOption="PDFS"
                    />
                </DialogContent>
            </Dialog>
            <Card classNames="bg-[--card-bg-gray]">
                {claim.claim_status === ClaimStatus.DENIED ||
                claim.claim_status === ClaimStatus.REJECTED ? (
                    <Alert
                        type="error"
                        title=" Reason for rejection"
                        description={claim.status_notes || "--"}
                        classNames="mb-[--spacing-header]"
                    />
                ) : null}

                {![ClaimStatus.DENIED, ClaimStatus.REJECTED].includes(
                    claim.claim_status as ClaimStatus
                ) && claim.status_notes ? (
                    <Alert
                        type="info"
                        title="Status Notes"
                        description={claim.status_notes || "--"}
                    />
                ) : null}
                <Card classNames="uppercase">
                    <div className="flex [border-bottom:1px_solid_var(--color-stroke)] mb-[--spacing-header] justify-between items-center pb-[--spacing-items] max-[678px]:flex-col max-[678px]:items-start">
                        <h2 className="flex gap-2 font-bold text-base items-center">
                            Personal & Insured Info
                            <Link
                                to={addPracticeParamToUrl(
                                    `/clients/${claim?.client_id}`,
                                    practice?.practice_id
                                )}
                                className="text-textLink text-caption underline ml font-semibold lowercase"
                                target="_blank"
                            >
                                view client chart
                            </Link>
                        </h2>

                        <div
                            className={cn(
                                "flex justify-end max-[678px]:mt-[--spacing-items] gap-x-[--spacing-items]"
                            )}
                        >
                            <div className="max-[678px]:ml-0">
                                <Button
                                    variant="secondary"
                                    onClick={handleViewClaim}
                                >
                                    View claim
                                </Button>
                            </div>
                            {!isCurrent &&
                            !(claim.is_voided && claim.voided_at_payer) ? (
                                <Button
                                    onClick={() => {
                                        handleChangeClaimVoidStatus(
                                            !claim.is_voided
                                        );
                                    }}
                                    disabled={changeVoidStatus.isLoading}
                                    variant="secondary"
                                >
                                    {claim.is_voided
                                        ? " Unvoid Claim"
                                        : "Void Claim"}
                                </Button>
                            ) : null}

                            {isCurrent && (
                                <div>
                                    <div>
                                        <Dropdown>
                                            <DropdownTrigger asChild>
                                                <FilterButton className="bg-primary text-white hover:bg-primary hover:brightness-90 ![border:none]">
                                                    Actions
                                                </FilterButton>
                                            </DropdownTrigger>
                                            <DropdownContent
                                                align="end"
                                                width="auto"
                                            >
                                                <>
                                                    {claim.claim_status ===
                                                        ClaimStatus.CLOSED && (
                                                        <DropdownItem
                                                            onClick={() =>
                                                                handleChangeStatus(
                                                                    ClaimStatus.PENDING
                                                                )
                                                            }
                                                            disabled={
                                                                patchClaimStatus.isLoading ||
                                                                isInMultiClaimGroup
                                                            }
                                                        >
                                                            Mark as Pending
                                                        </DropdownItem>
                                                    )}
                                                    {claim.claim_status !==
                                                    ClaimStatus.CLOSED ? (
                                                        <DropdownItem
                                                            onClick={
                                                                handleEditClick
                                                            }
                                                        >
                                                            Edit
                                                        </DropdownItem>
                                                    ) : null}

                                                    {claim.claim_status !==
                                                    ClaimStatus.CLOSED ? (
                                                        <DropdownItem
                                                            onClick={() => {
                                                                setShowPrompt({
                                                                    ...showPrompt,
                                                                    show: true,
                                                                    promptAction:
                                                                        "update_claim",
                                                                });
                                                            }}
                                                        >
                                                            Reset Claim
                                                        </DropdownItem>
                                                    ) : null}
                                                </>

                                                {claim.claim_status ===
                                                    ClaimStatus.READY_FOR_SUBMISSION && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleChangeStatus(
                                                                ClaimStatus.SUBMITTED
                                                            )
                                                        }
                                                        disabled={
                                                            isInMultiClaimGroup
                                                        }
                                                    >
                                                        Submit
                                                    </DropdownItem>
                                                )}
                                                {claim.claim_status !==
                                                    ClaimStatus.CLOSED &&
                                                claim.claim_status !==
                                                    ClaimStatus.READY_FOR_SUBMISSION &&
                                                claim.claim_status !==
                                                    ClaimStatus.ON_HOLD ? (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleChangeStatus(
                                                                ClaimStatus.SUBMITTED
                                                            )
                                                        }
                                                        disabled={
                                                            isInMultiClaimGroup
                                                        }
                                                    >
                                                        Resubmit
                                                    </DropdownItem>
                                                ) : null}
                                                {!claim.is_voided && (
                                                    <DropdownItem
                                                        onClick={() => {
                                                            handleChangeClaimVoidStatus(
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        Void Claim
                                                    </DropdownItem>
                                                )}
                                                {claim.is_voided &&
                                                    !claim.voided_at_payer && (
                                                        <DropdownItem
                                                            onClick={() => {
                                                                handleChangeClaimVoidStatus(
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            Unvoid Claim
                                                        </DropdownItem>
                                                    )}
                                                {/* to be uncommented when backend provides endpoint */}
                                                {/* {(status ===
                                                        ClaimStatus.RECEIVED ||
                                                        status ===
                                                            ClaimStatus.SUBMITTED ||
                                                        status ===
                                                            ClaimStatus.ACKNOWLEDGED ||
                                                        status ===
                                                            ClaimStatus.PENDING) &&
                                                        status && (
                                                            <DropdownItem>
                                                                Check status
                                                            </DropdownItem>
                                                        )} */}
                                            </DropdownContent>
                                        </Dropdown>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-y-[--spacing-items] max-[1000px]:grid-cols-1 *:leading-6">
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                insurance type
                            </p>
                            <p className=" text-sm font-medium uppercase">
                                {claim.claim_content.insuranceType
                                    ? removeEnumUnderscore(
                                          claim.claim_content.insuranceType
                                      )
                                    : "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                INSURED’s date of birth
                            </p>
                            <p className=" text-sm font-medium ">
                                {finalSubscriberDate
                                    ? format(finalSubscriberDate, "MM/dd/yyyy")
                                    : "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semiboldpb-8 ">
                                patient RELATIONSHIP TO INSURED
                            </p>
                            <p className=" text-sm font-medium ">
                                {relationshipMapping[
                                    claim?.claim_content
                                        .patientRelationshipToSubscriber as unknown as string
                                ] || "SELF"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                INSURED’s ID NUMBER
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.subscriberId}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold ">
                                INSURED’s SEX
                            </p>
                            <p className=" text-sm font-medium ">
                                {(claim.claim_content.subscriberSex === "M" &&
                                    "Male") ||
                                    (claim.claim_content.subscriberSex ===
                                        "F" &&
                                        "Female") ||
                                    (claim.claim_content.subscriberSex ===
                                        "U" &&
                                        "Unknown")}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                patient’s condition related to:
                            </p>
                            <div className="grid grid-cols-4 gap-y-6">
                                <div>
                                    <p className="text-textSec text-caption font-semibold">
                                        Employment
                                    </p>

                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .isConditionRelatedToEmployment ===
                                        "Y"
                                            ? "Yes"
                                            : "No"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-textSec text-caption font-semibold">
                                        Auto Accident
                                    </p>

                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .isConditionAutoAccidentRelated ===
                                        "Y"
                                            ? "Yes"
                                            : "No"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-textSec text-caption font-semibold">
                                        Other Accident
                                    </p>

                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .isConditionOtherAccidentRelated ===
                                        "Y"
                                            ? "Yes"
                                            : "No"}
                                    </p>
                                </div>
                                {claim.claim_content.autoAccidentState && (
                                    <div>
                                        <p className="text-textSec text-caption font-semibold">
                                            Other Description
                                        </p>
                                        <p className=" text-sm font-medium ">
                                            {
                                                claim.claim_content
                                                    .autoAccidentState
                                            }
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold ">
                                INSURED’s NAME
                            </p>
                            <p className=" text-sm font-medium ">
                                {`${
                                    claim?.claim_content.subscriberLastName &&
                                    claim?.claim_content.subscriberFirstName
                                        ? displayNameInRightFormat({
                                              firstName:
                                                  claim?.claim_content
                                                      .subscriberFirstName,
                                              lastName:
                                                  claim?.claim_content
                                                      .subscriberLastName,
                                          })
                                        : ""
                                } ${userInitials(
                                    claim?.claim_content
                                        ?.subscriberMiddleName || "",
                                    ""
                                )}`}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold ">
                                PATIENT’s NAME
                            </p>
                            <p className=" text-sm font-medium ">
                                {`${
                                    claim?.claim_content.patientLastName &&
                                    claim?.claim_content.patientFirstName
                                        ? displayNameInRightFormat({
                                              firstName:
                                                  claim?.claim_content
                                                      .patientFirstName,
                                              lastName:
                                                  claim?.claim_content
                                                      .patientLastName,
                                          })
                                        : ""
                                } ${userInitials(
                                    claim?.claim_content?.patientMiddleName ||
                                        "",
                                    ""
                                )}`}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Is there another health benefit plan
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content
                                    .otherInsurancePlanProgram === "" &&
                                claim.claim_content.otherSubscriberId === "" &&
                                claim.claim_content.otherSubscriberFirstName ===
                                    "" &&
                                claim.claim_content.otherSubscriberLastName ===
                                    ""
                                    ? "No"
                                    : "Yes"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold ">
                                INSURED’s Address
                            </p>
                            <div className=" text-sm font-medium   flex flex-wrap">
                                <div>
                                    <p className="w-[80%]">
                                        {claim.claim_content
                                            .subscriberAddress || "--"}
                                    </p>
                                    <div className="grid grid-cols-2 gap-y-6 mt-[6px]">
                                        <div>
                                            <p className="text-textSec text-caption font-semibold">
                                                CITY
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {claim.claim_content
                                                    .subscriberCity || "--"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-textSec text-caption font-semibold">
                                                STATE
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {getStateInShortForm(
                                                    claim.claim_content
                                                        .subscriberState
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-textSec text-caption font-semibold ">
                                                ZIPCODE
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {claim.claim_content
                                                    .subscriberZip || "--"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-textSec text-caption font-semibold ">
                                                TELEPHONE
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {claim.claim_content
                                                    .subscriberPhone || "--"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-6">
                                <p className="text-textSec text-caption font-semibold ">
                                    Insured&apos;s Policy Group or FECA number
                                </p>
                                <p className=" text-sm font-medium ">
                                    {claim.claim_content
                                        .subscriberPolicyGroup || "--"}
                                </p>
                            </div>
                            <div className="mt-6">
                                <p className="text-textSec text-caption font-semibold">
                                    Insurance Plan Name or Program Name
                                </p>
                                <div className=" text-sm font-medium ">
                                    {claim.claim_content.insuranceGroupName ||
                                        "--"}
                                </div>
                            </div>
                            <div className="mt-6">
                                <p className="text-textSec text-caption font-semibold">
                                    Other claim Id
                                    <span className="text-xss ">
                                        (Designated by Nucc)
                                    </span>
                                </p>
                                <div className=" text-sm font-medium ">
                                    {claim.claim_content.otherClaimId || "--"}
                                </div>
                            </div>
                        </div>

                        <div>
                            <p className="text-textSec text-caption font-semibold ">
                                PATIENT’s Address
                            </p>
                            <div className=" text-sm font-medium   flex flex-wrap">
                                <div>
                                    <p className="w-[80%]">
                                        {claim.claim_content.patientAddress ||
                                            "--"}
                                    </p>
                                    <div className="grid grid-cols-2 gap-x-10 gap-y-6 mt-[6px]">
                                        <div>
                                            <p className="text-textSec text-caption font-semibold">
                                                CITY
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {claim.claim_content
                                                    .patientCity || "--"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-textSec text-caption font-semibold ">
                                                STATE
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {getStateInShortForm(
                                                    claim.claim_content
                                                        .patientState
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-textSec text-caption font-semibold ">
                                                ZIPCODE
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {claim.claim_content
                                                    .patientZip || "--"}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-textSec text-caption font-semibold ">
                                                TELEPHONE
                                            </p>
                                            <p className=" text-sm font-medium ">
                                                {claim.claim_content
                                                    .patientPhone || "--"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-6">
                                <p className="text-textSec text-caption font-semibold ">
                                    PATIENT’s date of birth
                                </p>
                                <p className=" text-sm font-medium ">
                                    {`${formatDate(
                                        claim.claim_content
                                            .patientBirthdate as string,
                                        true
                                    )}` || "--"}
                                </p>
                            </div>
                            <div className="mt-6">
                                <p className="text-textSec text-caption font-semibold">
                                    PATIENT’s SEX
                                </p>
                                <div className=" text-sm font-medium ">
                                    {(claim.claim_content.patientSex === "M" &&
                                        "Male") ||
                                        (claim.claim_content.patientSex ===
                                            "F" &&
                                            "Female") ||
                                        (claim.claim_content.patientSex ===
                                            "U" &&
                                            "Unknown")}
                                </div>
                            </div>
                            <div className="mt-6">
                                <p className="text-textSec text-caption font-semibold">
                                    Claims codes
                                    <span className="text-xss ">
                                        (Designated by NUCC)
                                    </span>
                                </p>
                                <div className=" text-sm font-medium ">
                                    {claim.claim_content.claimCodes || "--"}
                                </div>
                            </div>
                            <div className="mt-6">
                                <p className="text-textSec text-caption font-semibold">
                                    Reserved for nucc use
                                </p>
                                <div className=" text-sm font-medium ">
                                    {claim.claim_content.reservedForNUCCUse ||
                                        "--"}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-row-2">
                            <div>
                                <p className="text-textSec text-caption font-semibold ">
                                    Patient’s or authorized person’s signature
                                </p>
                                <p className=" text-sm font-medium ">
                                    {`${
                                        claim.claim_content.patientSignature
                                    } ${formatDate(
                                        claim.claim_content
                                            .patientSignatureDate as string
                                    )}`}
                                </p>
                                <div className="mt-6">
                                    <p className="text-textSec text-caption font-semibold ">
                                        Insured’s or authorized person’s
                                        signature
                                    </p>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .subscriberSignature || ""}
                                    </p>
                                </div>
                                <div className="mt-6">
                                    <p className="text-textSec text-caption font-semibold ">
                                        Other Insured&apos;s Name
                                    </p>
                                    <p className=" text-sm font-medium ">
                                        {`${
                                            claim?.claim_content
                                                .otherSubscriberFirstName ||
                                            "--"
                                        } ${
                                            claim?.claim_content
                                                .otherSubscriberLastName || ""
                                        } ${userInitials(
                                            claim?.claim_content
                                                ?.otherSubscriberMiddleName ||
                                                "",
                                            ""
                                        )}`}
                                    </p>
                                </div>
                                <div className="mt-6">
                                    <p className="text-textSec text-caption font-semibold ">
                                        Other Insured Policy OR groUP nUMBER
                                    </p>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .otherSubscriberId || "--"}
                                    </p>
                                </div>
                                <div className="mt-6">
                                    <p className="text-textSec text-caption font-semibold ">
                                        Other Inusrance Plan Name or Program
                                    </p>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .otherInsurancePlanProgram || "--"}
                                    </p>
                                </div>
                                <div className="mt-6">
                                    <p className="text-textSec text-caption font-semibold ">
                                        Reserved for Nucc use
                                    </p>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .otherSubscriberReserved || "--"}
                                    </p>
                                </div>
                                <div className="mt-6">
                                    <p className="text-textSec text-caption font-semibold ">
                                        Reserved for Nucc use
                                    </p>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .otherSubscriberReserved2 || "--"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
                <Card classNames="mt-[--spacing-header] uppercase *:leading-6">
                    <div className="grid grid-cols-3 gap-y-[--spacing-items] max-[750px]:grid-cols-1">
                        <div className="text-xs font-semibold ">
                            <p className=" text-textSec ">
                                Date of current illness, injury or pregnancy
                                (LMP)
                            </p>
                            <div className="grid grid-cols-3 gap-y-[--spacing-items]">
                                <div>
                                    <span className="text-textSec">Date</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content.illnessDate ||
                                            "--"}
                                    </p>
                                </div>
                                <div className="text-textSec text-xs font-semibold">
                                    <span className="text-textSec"> Qual</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .illnessDateQualifier || "--"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className=" text-xs font-semibold ">
                            <span className="text-textSec"> OTHER DATE</span>
                            <div className="grid grid-cols-3 gap-y-4">
                                <div className="text-xs font-semibold">
                                    <span className="text-textSec "> Qual</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .otherDateQualifier || "--"}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-textSec"> Date</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content.otherDate || "--"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="text-xs font-semibold ">
                            <p className=" text-textSec ">
                                Dates Patient unable to work in current
                                occupation
                            </p>
                            <div className="grid grid-cols-3 gap-y-4">
                                <div className=" text-xs font-semibold">
                                    <span className="text-textSec">From</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .unableToWorkFromDate || "--"}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-textSec"> To</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .unableToWorkToDate || "--"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="text-xs font-semibold">
                            <p className=" text-textSec">
                                Hospitalization dates related to current service
                            </p>
                            <div className="grid grid-cols-3 gap-y-4">
                                <div className="text-xs font-semibold">
                                    <span className="text-textSec"> From</span>
                                    <p className="text-sm font-medium ">
                                        {claim.claim_content
                                            .admissionFromDate || "--"}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-textSec"> To</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content.admissionToDate ||
                                            "--"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className=" text-xs font-semibold ">
                            <p className=" text-textSec">
                                Referring Provider details
                            </p>
                            <div className="grid grid-cols-3 gap-y-4">
                                <div>
                                    <span className="text-textSec">
                                        Entity code
                                    </span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .referringProviderNumQual || "--"}
                                    </p>
                                </div>
                                <div className="text-xs font-semibold ">
                                    <span className="text-textSec ">
                                        Provider Name
                                    </span>
                                    <p className=" text-sm font-medium ">
                                        {`${
                                            claim?.claim_content
                                                .referringProviderLastName &&
                                            claim?.claim_content
                                                .referringProviderFirstName
                                                ? displayNameInRightFormat({
                                                      firstName:
                                                          claim?.claim_content
                                                              .referringProviderFirstName,
                                                      lastName:
                                                          claim?.claim_content
                                                              .referringProviderLastName,
                                                  })
                                                : ""
                                        }  ${userInitials(
                                            claim?.claim_content
                                                ?.referringProviderMiddleName ||
                                                "",
                                            ""
                                        )}`}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className=" text-xs font-semibold ">
                            <p className=" text-textSec">
                                Referring Provider Additional Details
                            </p>
                            <div className="grid grid-cols-3 gap-y-1 gap-x-1">
                                <div className=" text-xs font-semibold">
                                    <span className="text-textSec">
                                        Additional Id
                                    </span>
                                    <p className="text-sm font-medium ">
                                        {claim.claim_content
                                            .referringProviderAdditionalId ||
                                            "--"}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-textSec">
                                        Additional Id Qual
                                    </span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .referringProviderAdditionalIdQual ||
                                            "--"}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-textSec"> Npi</span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .referringProviderNPI || "--"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className=" text-xs font-semibold ">
                            <p className="text-textSec">
                                {" "}
                                Additional Claim info{" "}
                                <span className="text-xss ">
                                    (Designated by NUCC)
                                </span>
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.additionalClaimInfo ||
                                    "--"}
                            </p>
                        </div>
                        <div className=" text-xs font-semibold ">
                            <p className=" text-textSec">
                                Resubmission details
                            </p>
                            <div className="grid grid-cols-3 gap-y-4">
                                <div className="text-xs font-semibold ">
                                    <span className="text-textSec ">
                                        Resubmission Code
                                    </span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content.resubmissionCode ||
                                            "--"}
                                    </p>
                                </div>
                                <div className="">
                                    <span className="text-textSec">
                                        Original ref no
                                    </span>
                                    <p className=" text-sm font-medium ">
                                        {claim.claim_content
                                            .originalClaimReferenceNumber ||
                                            "--"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="text-xs font-semibold ">
                            <span className="text-textSec ">
                                Prior Authorization code
                            </span>

                            <p className=" text-sm font-medium ">
                                {claim.claim_content.priorAuthorizationNumber ||
                                    "--"}
                            </p>
                        </div>
                    </div>
                </Card>
                <Card classNames="mt-[--spacing-header] uppercase *:leading-6">
                    <h2 className="font-bold  text-sm flex items-center gap-8 pb-24">
                        DIAGNOSIS INFO
                    </h2>
                    <div className="grid grid-cols-3 gap-y-4">
                        <div className=" text-xs font-semibold ">
                            <span className="text-textSec"> Outside lab?</span>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.outsideLabCharge === ""
                                    ? "No"
                                    : "Yes"}
                            </p>
                        </div>
                        {claim.claim_content.outsideLabCharge === "" ? null : (
                            <div className="text-xs font-semibold ">
                                <span className="text-textSec">
                                    Dollar charges
                                </span>
                                <p className=" text-sm font-medium ">
                                    {showMoneyInAppFormat(
                                        claim.claim_content.outsideLabCharge ||
                                            "0"
                                    )}
                                </p>
                            </div>
                        )}
                        <div className="col-span-2">
                            <div className="grid grid-cols-2 text-xs gap-x-3">
                                <span className="text-textSec">
                                    Diagnosis or Nature of Illness or Injury
                                </span>
                                <span className="text-textSec">
                                    Diagnosis Pointer
                                </span>
                            </div>

                            <div className="flex flex-col gap-y-3">
                                {getDiagnosisCodes()?.map((diagnosis) => (
                                    <div
                                        key={diagnosis?.code}
                                        className="grid grid-cols-2 gap-x-3"
                                    >
                                        <p className="text-sm font-medium ">
                                            {`${diagnosis?.code} ${diagnosis?.description} `}
                                        </p>
                                        <p
                                            className="text-sm font-medium "
                                            key={diagnosis?.code}
                                        >
                                            {diagnosis?.pointer}
                                        </p>
                                    </div>
                                )) || "--"}
                            </div>
                        </div>
                    </div>
                </Card>
                <Card classNames="mt-[--spacing-header] *:leading-6">
                    <div className="flex justify-between mb-[--spacing-header]">
                        <h2 className="font-bold text-sm flex items-center gap-2 pb-[--spacing-items] uppercase">
                            ATTACHED DOCUMENTS
                        </h2>
                        <Button
                            onClick={() => setUploadNewDoc(true)}
                            className="h-8"
                        >
                            <div className="flex gap-2 items-center">
                                <PlusIcon /> <span>Add new Doc</span>
                            </div>
                        </Button>
                    </div>
                    {fetchClaimsAttachments &&
                    fetchClaimsAttachments?.data &&
                    fetchClaimsAttachments?.data?.data?.length > 0 ? (
                        <div>
                            <Table
                                columns={attachedDocumentsColumns}
                                data={fetchClaimsAttachments?.data?.data || []}
                                className={styles.attached_document_table}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col justify-center items-center">
                            <p className="font-medium text-sm">
                                No attached doc
                            </p>
                            <p className="text-textSec text-sm font-normal w-[206px] pt-4">
                                Click the &quot;upload file&quot; button to
                                attach a file to this claim.
                            </p>
                            <Button
                                variant="secondary"
                                className="mt-16"
                                onClick={() => setUploadNewDoc(true)}
                            >
                                Upload File
                            </Button>
                        </div>
                    )}
                </Card>
                <Card classNames="*:leading-6 mt-[--spacing-header] uppercase">
                    <h2 className="font-bold  text-sm flex items-center gap-2 pb-24">
                        SERVICES
                    </h2>
                    <Table
                        columns={columns}
                        data={claim?.claim_content?.serviceDetails}
                        className={styles.table}
                    />
                </Card>
                <Card classNames="mt-[--spacing-header] *:leading-6 uppercase">
                    <div className="grid grid-cols-3 gap-y-6 max-[750px]:grid-cols-1">
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Federal Tax I.D Number
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.federalTaxId || "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Total charge
                            </p>
                            <p className=" text-sm font-medium ">
                                {showMoneyInAppFormat(
                                    claim.claim_content.totalCharge || 0
                                )}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Service Facility Location Information
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.facilityAddress1 || "--"}
                                {claim.claim_content.facilityAddress2 || ""}
                                {claim.claim_content.facilityCity || ""}
                                {claim.claim_content.facilityState || ""}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Service Facility NPI
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.facilityNPI || "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Service Facility Provider Number
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.facilityProviderNumber ||
                                    "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Billing provider info & PH#
                            </p>
                            <p className=" text-sm font-medium ">
                                {`${
                                    claim.claim_content.billingProviderPhone ||
                                    "--"
                                } ${
                                    claim.claim_content
                                        .billingProviderAddress1 || " "
                                } ${
                                    claim.claim_content
                                        .billingProviderAddress2 || " "
                                } ${
                                    claim.claim_content.billingProviderCity ||
                                    " "
                                } ${
                                    claim.claim_content.billingProviderState ||
                                    " "
                                } `}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Billing provider Npi
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.billingProviderNPI || "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Billing Provider Number
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.billingProviderNumber ||
                                    "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Patient’s account number
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content
                                    .claimReferenceOrPatientAcctNo || "--"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                amount paid
                            </p>
                            <p className=" text-sm font-medium ">
                                {showMoneyInAppFormat(
                                    claim.claim_content.amountPaid || 0
                                )}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                assignment AcceptED
                            </p>
                            <p className=" text-sm font-medium ">
                                {claim.claim_content.acceptAssignment === "Y"
                                    ? "Yes"
                                    : "No"}
                            </p>
                        </div>
                        <div>
                            <p className="text-textSec text-caption font-semibold">
                                Signature Of Physician and supplier
                            </p>
                            <p className=" text-sm font-medium ">
                                {`${
                                    claim.claim_content
                                        .referringProviderFirstName &&
                                    claim.claim_content
                                        .referringProviderLastName
                                        ? displayNameInRightFormat({
                                              firstName:
                                                  claim.claim_content
                                                      .referringProviderFirstName,
                                              lastName:
                                                  claim.claim_content
                                                      .referringProviderLastName,
                                          })
                                        : ""
                                } ${
                                    claim.claim_content
                                        .renderingProviderSignatureDate
                                }`}
                            </p>
                        </div>
                    </div>
                </Card>
            </Card>
        </div>
    );
}
